import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  changeQueue,
  checkForUpdates,
  isDesktop,
  onTrayAction,
  setPendingBadge,
} from '@/lib/desktop';
import { useToast } from '@/components/Toast';

const TRAY_ROUTES: Record<string, string> = {
  'my-day': '/',
  'new-task': '/?compose=task',
};

const UPDATE_CHECK_INTERVAL = 6 * 60 * 60 * 1000;
const BADGE_REFRESH_INTERVAL = 30 * 1000;

/**
 * Desktop-only wiring: tray menu routing, the update check and the tray badge
 * showing how many offline edits are still waiting. No-op in the browser.
 */
export function useDesktopIntegration(): void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    if (!isDesktop) return;

    let unlisten: (() => void) | undefined;
    void onTrayAction((action) => {
      if (action === 'sync') {
        void queryClient.invalidateQueries();
        toast.push({ tone: 'info', title: 'Оновлюємо дані…' });
        return;
      }
      const route = TRAY_ROUTES[action];
      if (route) navigate(route);
    }).then((dispose) => {
      unlisten = dispose;
    });

    return () => unlisten?.();
  }, [navigate, queryClient, toast]);

  useEffect(() => {
    if (!isDesktop) return;

    const run = () => {
      void checkForUpdates().then((version) => {
        if (version) toast.success(`Оновлено до версії ${version}`);
      });
    };

    run();
    const timer = window.setInterval(run, UPDATE_CHECK_INTERVAL);
    return () => window.clearInterval(timer);
  }, [toast]);

  useEffect(() => {
    if (!isDesktop) return;

    const refresh = () => {
      void changeQueue.list().then((queued) => setPendingBadge(queued.length));
    };

    refresh();
    const timer = window.setInterval(refresh, BADGE_REFRESH_INTERVAL);
    return () => window.clearInterval(timer);
  }, []);
}
